import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDownIcon, ChevronUpIcon, EyeIcon, EyeOffIcon, GripVerticalIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/cn';
import {
  arrangeItems,
  layoutOf,
  MENU_SECTIONS,
  moveItem,
  sectionOf,
  type MenuLayout,
} from '@/shared/registry/menuLayout';
import type { NavItem, NavSection } from '@/shared/registry/types';

export interface MenuEditorProps {
  /** The items to arrange, in their natural order (the registry's, already limited to what the person may open). */
  items: readonly NavItem[];
  /** The arrangement being edited (null: the natural order, nothing hidden). */
  layout: MenuLayout | null;
  /** Called with the complete new arrangement after every drag, step or toggle. */
  onChange: (layout: MenuLayout) => void;
  /** Labels the section of the administrators' items (the sections `primary` and `main` have none). */
  adminHeading?: string;
  className?: string;
}

/**
 * The left menu's items in their sections, each with a handle to drag (or Space + arrows), «up» and «down» buttons and a switch that
 * hides it: the one editor behind «Customize menu» (a person's own arrangement) and the administration's page of the default menu.
 * It only edits an arrangement; saving is the caller's.
 */
export function MenuEditor({ items, layout, onChange, adminHeading, className }: MenuEditorProps) {
  const { t } = useTranslation('shared');
  const arranged = useMemo(() => arrangeItems(items, layout), [items, layout]);
  const hidden = useMemo(() => new Set(layout?.hidden ?? []), [layout]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const commit = (next: readonly NavItem[], nextHidden: ReadonlySet<string> = hidden) =>
    onChange(layoutOf(next, nextHidden));
  const toggle = (id: string) => {
    const next = new Set(hidden);
    if (!next.delete(id)) next.add(id);
    commit(arranged, next);
  };
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) commit(moveItem(arranged, String(active.id), String(over.id)));
  };
  const step = (id: string, direction: -1 | 1) => {
    const item = arranged.find((candidate) => candidate.id === id);
    if (!item) return;
    const inSection = arranged.filter((candidate) => sectionOf(candidate) === sectionOf(item));
    const neighbour = inSection[inSection.indexOf(item) + direction];
    if (neighbour) commit(moveItem(arranged, id, neighbour.id));
  };

  const heading = (section: NavSection) =>
    section === 'admin'
      ? (adminHeading ?? t('menuEditor.sections.admin'))
      : t(`menuEditor.sections.${section}`);

  return (
    <div className={cn('space-y-5', className)} data-testid="menu-editor">
      {MENU_SECTIONS.map((section) => {
        const inSection = arranged.filter((item) => sectionOf(item) === section);
        if (inSection.length === 0) return null;
        const headingId = `menu-editor-${section}`;
        return (
          <section key={section} aria-labelledby={headingId} data-section={section}>
            <h3
              id={headingId}
              className="mb-1.5 text-[0.6875rem] font-semibold tracking-wide text-muted-foreground uppercase"
            >
              {heading(section)}
            </h3>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext
                items={inSection.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <ul className="space-y-1">
                  {inSection.map((item, index) => (
                    <EditorRow
                      key={item.id}
                      item={item}
                      hidden={hidden.has(item.id)}
                      first={index === 0}
                      last={index === inSection.length - 1}
                      onToggle={() => toggle(item.id)}
                      onStep={(direction) => step(item.id, direction)}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          </section>
        );
      })}
    </div>
  );
}

function EditorRow({
  item,
  hidden,
  first,
  last,
  onToggle,
  onStep,
}: {
  item: NavItem;
  hidden: boolean;
  first: boolean;
  last: boolean;
  onToggle: () => void;
  onStep: (direction: -1 | 1) => void;
}) {
  const { t } = useTranslation('shared');
  const { t: tItem } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const label = tItem(item.labelKey);
  const Icon = item.icon;
  const iconButton =
    'inline-flex size-7 items-center justify-center rounded-md text-muted-foreground focus-ring transition-colors hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-35 [&_svg]:size-4';
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      data-menu-item={item.id}
      data-hidden={hidden}
      className={cn(
        'flex items-center gap-1.5 rounded-md border bg-card py-1 pr-1 pl-0.5 text-sm',
        isDragging && 'relative z-10 shadow-md',
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={t('menuEditor.drag', { label })}
        className={cn(iconButton, 'cursor-grab touch-none active:cursor-grabbing')}
      >
        <GripVerticalIcon aria-hidden />
      </button>
      <Icon
        className={cn('size-4 shrink-0', hidden ? 'text-subtle-foreground' : 'text-muted-foreground')}
        aria-hidden
      />
      <span className={cn('min-w-0 flex-1 truncate', hidden && 'text-muted-foreground line-through')}>
        {label}
      </span>
      {hidden && (
        <span className="rounded-full bg-muted px-1.5 text-[0.6875rem] text-muted-foreground">
          {t('menuEditor.hiddenBadge')}
        </span>
      )}
      <button
        type="button"
        className={iconButton}
        disabled={first}
        onClick={() => onStep(-1)}
        aria-label={t('menuEditor.moveUp', { label })}
      >
        <ChevronUpIcon aria-hidden />
      </button>
      <button
        type="button"
        className={iconButton}
        disabled={last}
        onClick={() => onStep(1)}
        aria-label={t('menuEditor.moveDown', { label })}
      >
        <ChevronDownIcon aria-hidden />
      </button>
      <button
        type="button"
        className={iconButton}
        onClick={onToggle}
        aria-pressed={hidden}
        aria-label={hidden ? t('menuEditor.show', { label }) : t('menuEditor.hide', { label })}
      >
        {hidden ? <EyeOffIcon aria-hidden /> : <EyeIcon aria-hidden />}
      </button>
    </li>
  );
}
